import React, { useState, useEffect } from 'react'
import { useParams, useNavigate, Link } from 'react-router-dom'
import { getEvent, updateEvent, deleteEvent } from '../../services/events'
import { uploadEventImage } from '../../services/upload'
import { getUser } from '../../services/helpers'
import {
  CalendarIcon,
  MapPinIcon,
  ClockIcon,
  UsersIcon,
  TagIcon,
  PencilIcon,
  TrashIcon,
  ArrowLeftIcon,
  PhotoIcon,
  CheckIcon,
  XMarkIcon,
  BuildingOfficeIcon,
  Bars3Icon,
  ChartBarIcon,
  ShoppingBagIcon,
  TicketIcon,
  WalletIcon,
  UserCircleIcon,
  ExclamationTriangleIcon,
  CheckCircleIcon
} from '@heroicons/react/24/outline'

const CenterEventDetailPage = () => {
  const { eventId } = useParams()
  const navigate = useNavigate()
  const [user, setUser] = useState(getUser())
  const displayName = user?.organization || user?.name || 'Event Center'
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false)
  const [event, setEvent] = useState(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')
  const [success, setSuccess] = useState('')
  const [isEditing, setIsEditing] = useState(false)
  const [deleting, setDeleting] = useState(false)
  const [saving, setSaving] = useState(false)
  
  const [eventForm, setEventForm] = useState({
    title: '',
    description: '',
    venue: '',
    location: {
      city: '',
      country: ''
    },
    startsAt: '',
    endsAt: '',
    slots: '',
    coverImage: '',
    tags: '',
    status: 'DRAFT'
  })
  
  const [imageFile, setImageFile] = useState(null)
  const [imagePreview, setImagePreview] = useState(null)
  const [uploadingImage, setUploadingImage] = useState(false)

  const tabs = [
    { id: 'overview', label: 'Overview', icon: ChartBarIcon, path: '/center' },
    { id: 'events', label: 'My Events', icon: CalendarIcon, path: '/center/events', disabled: true },
    { id: 'offers', label: 'My Offers', icon: TagIcon, path: '/center/offers' },
    { id: 'orders', label: 'Orders', icon: ShoppingBagIcon, path: '/center/orders' },
    { id: 'tickets', label: 'Tickets', icon: TicketIcon, path: '/center/tickets' },
    { id: 'media', label: 'Media Gallery', icon: PhotoIcon, path: '/center/media', disabled: true },
    { id: 'disputes', label: 'Disputes', icon: ExclamationTriangleIcon, path: '/center/disputes' },
    { id: 'wallet', label: 'Wallet', icon: WalletIcon, path: '/center/wallet' },
    { id: 'profile', label: 'Profile', icon: UserCircleIcon, path: '/center' }
  ]

  useEffect(() => {
    fetchEvent()
  }, [eventId])

  const fetchEvent = async () => {
    try {
      setLoading(true)
      setError('')
      const res = await getEvent(eventId)
      if (res.status === 200) {
        const eventData = res.data.event || res.data
        setEvent(eventData)
        // Populate form for editing
        const startDate = new Date(eventData.startsAt)
        const endDate = new Date(eventData.endsAt)
        setEventForm({
          title: eventData.title || '',
          description: eventData.description || '',
          venue: eventData.venue || '',
          location: {
            city: eventData.location?.city || '',
            country: eventData.location?.country || ''
          },
          startsAt: startDate.toISOString().slice(0, 16),
          endsAt: endDate.toISOString().slice(0, 16),
          slots: eventData.slots || '',
          coverImage: eventData.coverImage || '',
          tags: Array.isArray(eventData.tags) ? eventData.tags.join(', ') : (eventData.tags || ''),
          status: eventData.status || 'DRAFT'
        })
        if (eventData.coverImage) {
          setImagePreview(eventData.coverImage)
        }
      } else {
        setError(res.error || 'Failed to load event')
      }
      setLoading(false)
    } catch (err) {
      setError('Failed to load event')
      setLoading(false)
    }
  }

  const handleEventChange = (e) => {
    const { name, value } = e.target
    if (name.includes('.')) {
      const [parent, child] = name.split('.')
      setEventForm(prev => ({
        ...prev,
        [parent]: {
          ...prev[parent],
          [child]: value
        }
      }))
    } else {
      setEventForm(prev => ({ ...prev, [name]: value }))
    }
  }

  const handleImageChange = async (e) => {
    const file = e.target.files[0]
    if (!file) return

    if (!file.type.startsWith('image/')) {
      setError('Please select a valid image file')
      return
    }

    if (file.size > 10 * 1024 * 1024) {
      setError('Image size must be less than 10MB')
      return
    }

    setImageFile(file)
    setError('')

    const reader = new FileReader()
    reader.onloadend = () => {
      setImagePreview(reader.result)
    }
    reader.readAsDataURL(file)

    setUploadingImage(true)
    try {
      const res = await uploadEventImage(file)
      if (res.status === 200 && res.data?.imageUrl) {
        setEventForm(prev => ({ ...prev, coverImage: res.data.imageUrl }))
        setSuccess('Image uploaded successfully!')
        setTimeout(() => setSuccess(''), 3000)
      } else {
        setError(res.error || 'Failed to upload image')
        setImageFile(null)
        setImagePreview(null)
      }
    } catch (err) {
      setError('Failed to upload image. Please try again.')
      setImageFile(null)
      setImagePreview(null)
    } finally {
      setUploadingImage(false)
    }
  }

  const handleUpdate = async (e) => {
    e.preventDefault()
    setSaving(true)
    setError('')
    setSuccess('')
    
    try {
      const params = new URLSearchParams()
      
      params.append('title', eventForm.title)
      params.append('description', eventForm.description)
      params.append('venue', eventForm.venue)
      params.append('location.city', eventForm.location.city || '')
      params.append('location.country', eventForm.location.country || '')
      params.append('startsAt', new Date(eventForm.startsAt).toISOString())
      params.append('endsAt', new Date(eventForm.endsAt).toISOString())
      params.append('slots', eventForm.slots)
      params.append('status', eventForm.status || 'DRAFT')
      if (eventForm.coverImage) params.append('coverImage', eventForm.coverImage)
      if (eventForm.tags) {
        eventForm.tags.split(',').forEach(tag => {
          params.append('tags[]', tag.trim())
        })
      }
      
      const res = await updateEvent(eventId, params.toString())
      
      if (res.status === 200) {
        setSuccess('Event updated successfully!')
        setIsEditing(false)
        fetchEvent()
        setTimeout(() => setSuccess(''), 3000)
      } else {
        setError(res.error || 'Failed to update event')
      }
    } catch (err) {
      setError('An error occurred while updating event')
    } finally {
      setSaving(false)
    }
  }

  const handleDelete = async () => {
    if (!window.confirm('Are you sure you want to delete this event? This action cannot be undone.')) {
      return
    }

    setDeleting(true)
    setError('')
    
    try {
      const res = await deleteEvent(eventId)
      if (res.status === 200) {
        navigate('/center/events')
      } else {
        setError(res.error || 'Failed to delete event')
      }
    } catch (err) {
      setError('An error occurred while deleting event')
    } finally {
      setDeleting(false)
    }
  }

  const getStatusBadge = (status) => {
    const badges = {
      DRAFT: 'bg-gray-100 text-gray-700',
      PUBLISHED: 'bg-green-100 text-green-700',
      ARCHIVED: 'bg-yellow-100 text-yellow-700',
      CANCELLED: 'bg-red-100 text-red-700'
    }
    return badges[status] || 'bg-gray-100 text-gray-700'
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-emerald-600 mx-auto mb-4"></div>
          <p className="text-slate-600">Loading event...</p>
        </div>
      </div>
    )
  }

  if (error && !event) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <div className="text-center max-w-md mx-auto px-4">
          <p className="text-lg font-semibold text-slate-900 mb-2">Event not found</p>
          <p className="text-slate-600 mb-6">{error}</p>
          <Link
            to="/center/events"
            className="inline-flex items-center gap-2 text-emerald-600 hover:text-emerald-700 font-medium"
          >
            <ArrowLeftIcon className="w-5 h-5" />
            Back to Events
          </Link>
        </div>
      </div>
    )
  }

  if (!event) return null

  return (
    <div className="min-h-screen bg-slate-50 flex font-sans">
      {/* Sidebar Navigation */}
      <aside className={`fixed inset-y-0 left-0 z-50 w-64 bg-white border-r border-slate-200 transform transition-transform duration-300 ease-in-out lg:translate-x-0 ${mobileMenuOpen ? 'translate-x-0' : '-translate-x-full'}`}>
        <div className="flex flex-col h-full">
          {/* Logo */}
          <div className="flex items-center gap-3 px-6 h-16 border-b border-slate-100">
            <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-emerald-600 to-emerald-500 flex items-center justify-center text-white shadow-sm">
              <BuildingOfficeIcon className="w-5 h-5" />
            </div>
            <span className="text-xl font-bold text-slate-900 tracking-tight">Obule Panel</span>
            <button onClick={() => setMobileMenuOpen(false)} className="ml-auto lg:hidden">
              <XMarkIcon className="w-6 h-6 text-slate-400" />
            </button>
          </div>

          {/* Navigation */}
          <nav className="flex-1 px-3 py-6 space-y-1 overflow-y-auto">
            {tabs.filter(tab => !tab.disabled).map((tab) => {
              const Icon = tab.icon
              const isActive = tab.id === 'events'
              
              return (
                <Link
                  key={tab.id}
                  to={tab.path}
                  onClick={() => setMobileMenuOpen(false)}
                  className={`w-full flex items-center gap-3 px-3 py-2.5 text-sm font-medium rounded-lg transition-all duration-200 ${
                    isActive
                      ? 'bg-emerald-50 text-emerald-700'
                      : 'text-slate-600 hover:bg-slate-50 hover:text-slate-900'
                  }`}
                >
                  <Icon className={`w-5 h-5 ${isActive ? 'text-emerald-600' : 'text-slate-400'}`} />
                  {tab.label}
                </Link>
              )
            })}
          </nav>

          {/* User Profile */}
          <div className="p-4 border-t border-slate-100">
            <div className="flex items-center gap-3 mb-3">
              <div className="w-10 h-10 rounded-full bg-gradient-to-br from-emerald-500 to-blue-500 flex items-center justify-center text-white font-bold">
                {displayName.charAt(0).toUpperCase()}
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-semibold text-slate-900 truncate">{displayName}</p>
                <p className="text-xs text-slate-500 truncate">{user?.email}</p>
              </div>
            </div>
          </div>
        </div>
      </aside>

      {/* Main Content */}
      <main className="flex-1 lg:ml-64">
        <div className="p-6 lg:p-8 max-w-7xl mx-auto">
          {/* Mobile Menu Button */}
          <button
            onClick={() => setMobileMenuOpen(true)}
            className="lg:hidden fixed top-4 left-4 z-40 p-2 bg-white rounded-lg shadow-md border border-slate-200"
          >
            <Bars3Icon className="w-6 h-6 text-slate-600" />
          </button>

          {/* Header */}
          <div className="mb-6">
            <Link
              to="/center/events"
              className="inline-flex items-center gap-2 text-slate-600 hover:text-slate-900 mb-4 transition-colors"
            >
              <ArrowLeftIcon className="w-5 h-5" />
              Back to Events
            </Link>

            {/* Messages */}
            {error && (
              <div className="mb-4 p-4 bg-red-50 border border-red-200 rounded-lg text-red-700">
                {error}
              </div>
            )}
            {success && (
              <div className="mb-4 p-4 bg-green-50 border border-green-200 rounded-lg text-green-700">
                {success}
              </div>
            )}

            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
              <div>
                <h1 className="text-3xl font-bold text-slate-900 mb-2">
                  {isEditing ? 'Edit Event' : event.title}
                </h1>
                <div className="flex items-center gap-3 flex-wrap">
                  <span className={`px-3 py-1 rounded-full text-xs font-bold ${getStatusBadge(event.status)}`}>
                    {event.status}
                  </span>
                </div>
              </div>
              {!isEditing && (
                <div className="flex gap-3">
                  <button
                    onClick={() => setIsEditing(true)}
                    className="inline-flex items-center gap-2 px-4 py-2 bg-white border border-slate-300 text-slate-700 rounded-lg hover:bg-slate-50 transition-colors font-medium"
                  >
                    <PencilIcon className="w-5 h-5" />
                    Edit
                  </button>
                  <button
                    onClick={handleDelete}
                    disabled={deleting}
                    className="inline-flex items-center gap-2 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors font-medium disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    <TrashIcon className="w-5 h-5" />
                    {deleting ? 'Deleting...' : 'Delete'}
                  </button>
                </div>
              )}
            </div>
          </div>

          {isEditing ? (
            /* Edit Form */
            <div className="bg-white rounded-2xl border border-slate-200 p-8">
              <form onSubmit={handleUpdate} className="space-y-6">
                <div>
                  <label className="block text-sm font-semibold text-slate-900 mb-2">Event Title *</label>
                  <input
                    name="title"
                    value={eventForm.title}
                    onChange={handleEventChange}
                    required
                    minLength={3}
                    maxLength={200}
                    className="w-full rounded-xl px-4 py-3 bg-white border border-slate-300 text-slate-900 placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-transparent transition-all"
                    placeholder="e.g., Summer Music Festival 2024"
                  />
                </div>
                <div>
                  <label className="block text-sm font-semibold text-slate-900 mb-2">Description *</label>
                  <textarea
                    name="description"
                    value={eventForm.description}
                    onChange={handleEventChange}
                    required
                    minLength={10}
                    rows="4"
                    className="w-full rounded-xl px-4 py-3 bg-white border border-slate-300 text-slate-900 placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-transparent transition-all resize-none"
                    placeholder="Describe your event in detail..."
                  />
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label className="block text-sm font-semibold text-slate-900 mb-2">Venue *</label>
                    <input
                      name="venue"
                      value={eventForm.venue}
                      onChange={handleEventChange}
                      required
                      className="w-full rounded-xl px-4 py-3 bg-white border border-slate-300 text-slate-900 placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-transparent transition-all"
                      placeholder="Event venue name"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-semibold text-slate-900 mb-2">City</label>
                    <input
                      name="location.city"
                      value={eventForm.location.city}
                      onChange={handleEventChange}
                      className="w-full rounded-xl px-4 py-3 bg-white border border-slate-300 text-slate-900 placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-transparent transition-all"
                      placeholder="City"
                    />
                  </div>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label className="block text-sm font-semibold text-slate-900 mb-2">Start Date & Time *</label>
                    <input
                      name="startsAt"
                      type="datetime-local"
                      value={eventForm.startsAt}
                      onChange={handleEventChange}
                      required
                      className="w-full rounded-xl px-4 py-3 bg-white border border-slate-300 text-slate-900 focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-transparent transition-all"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-semibold text-slate-900 mb-2">End Date & Time *</label>
                    <input
                      name="endsAt"
                      type="datetime-local"
                      value={eventForm.endsAt}
                      onChange={handleEventChange}
                      required
                      className="w-full rounded-xl px-4 py-3 bg-white border border-slate-300 text-slate-900 focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-transparent transition-all"
                    />
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-semibold text-slate-900 mb-2">Total Slots (Capacity) *</label>
                  <input
                    name="slots"
                    type="number"
                    value={eventForm.slots}
                    onChange={handleEventChange}
                    required
                    min="1"
                    className="w-full rounded-xl px-4 py-3 bg-white border border-slate-300 text-slate-900 placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-transparent transition-all"
                    placeholder="Maximum number of attendees"
                  />
                </div>
                <div>
                  <label className="block text-sm font-semibold text-slate-900 mb-2 flex items-center">
                    <PhotoIcon className="w-4 h-4 mr-2" />
                    Cover Image
                  </label>
                  {imagePreview && (
                    <div className="mb-4">
                      <img 
                        src={imagePreview} 
                        alt="Preview" 
                        className="w-full h-64 object-cover rounded-xl border border-slate-300"
                      />
                    </div>
                  )}
                  <div className="relative">
                    <input
                      type="file"
                      accept="image/*"
                      onChange={handleImageChange}
                      disabled={uploadingImage}
                      className="w-full rounded-xl px-4 py-3 bg-white border border-slate-300 text-slate-900 file:mr-4 file:py-2 file:px-4 file:rounded-lg file:border-0 file:text-sm file:font-semibold file:bg-emerald-600 file:text-white hover:file:bg-emerald-700 focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-transparent transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                    />
                    {uploadingImage && (
                      <div className="absolute right-4 top-1/2 transform -translate-y-1/2">
                        <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-emerald-600"></div>
                      </div>
                    )}
                  </div>
                  <p className="text-xs text-slate-500 mt-2">Upload an image (JPG, PNG, WEBP - Max 10MB)</p>
                </div>
                <div>
                  <label className="block text-sm font-semibold text-slate-900 mb-2">Tags (comma-separated)</label>
                  <input
                    name="tags"
                    value={eventForm.tags}
                    onChange={handleEventChange}
                    className="w-full rounded-xl px-4 py-3 bg-white border border-slate-300 text-slate-900 placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-transparent transition-all"
                    placeholder="music, festival, outdoor"
                  />
                </div>
                <div>
                  <label className="block text-sm font-semibold text-slate-900 mb-2">Status *</label>
                  <select
                    name="status"
                    value={eventForm.status}
                    onChange={handleEventChange}
                    required
                    className="w-full rounded-xl px-4 py-3 bg-white border border-slate-300 text-slate-900 focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-transparent transition-all"
                  >
                    <option value="DRAFT">Draft</option>
                    <option value="PUBLISHED">Published</option>
                    <option value="ARCHIVED">Archived</option>
                  </select>
                  <p className="text-xs text-slate-500 mt-2">Choose the status for your event</p>
                </div>
                <div className="flex gap-3">
                  <button
                    type="submit"
                    disabled={saving}
                    className="flex-1 rounded-xl py-4 bg-emerald-600 text-white font-semibold shadow-lg hover:bg-emerald-700 hover:shadow-xl disabled:opacity-60 disabled:cursor-not-allowed transition-all duration-200"
                  >
                    {saving ? 'Saving...' : 'Save Changes'}
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      setIsEditing(false)
                      fetchEvent()
                    }}
                    className="px-6 py-4 rounded-xl bg-white border border-slate-300 text-slate-700 font-semibold hover:bg-slate-50 transition"
                  >
                    Cancel
                  </button>
                </div>
              </form>
            </div>
          ) : (
            /* View Mode */
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
              {/* Main Content */}
              <div className="lg:col-span-2 space-y-6">
                {/* Cover Image */}
                {event.coverImage && (
                  <div className="bg-white rounded-2xl border border-slate-200 overflow-hidden">
                    <img 
                      src={event.coverImage} 
                      alt={event.title}
                      className="w-full h-64 object-cover"
                      onError={(e) => {
                        e.target.style.display = 'none'
                      }}
                    />
                  </div>
                )}

                {/* Event Details Card */}
                <div className="bg-white rounded-2xl border border-slate-200 p-8">
                  <h2 className="text-2xl font-bold text-slate-900 mb-6">Event Details</h2>
                  <div className="space-y-4">
                    <div>
                      <h3 className="text-sm font-semibold text-slate-600 mb-2">Description</h3>
                      <p className="text-slate-900 leading-relaxed">{event.description}</p>
                    </div>
                    
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 pt-4 border-t border-slate-200">
                      <div className="flex items-start space-x-3">
                        <MapPinIcon className="w-5 h-5 text-emerald-600 mt-1 flex-shrink-0" />
                        <div>
                          <p className="text-sm text-slate-600">Venue</p>
                          <p className="text-slate-900 font-medium">{event.venue}</p>
                          {event.location?.city && (
                            <p className="text-sm text-slate-500">
                              {event.location.city}
                              {event.location.country && `, ${event.location.country}`}
                            </p>
                          )}
                        </div>
                      </div>
                      
                      <div className="flex items-start space-x-3">
                        <CalendarIcon className="w-5 h-5 text-emerald-600 mt-1 flex-shrink-0" />
                        <div>
                          <p className="text-sm text-slate-600">Start Date</p>
                          <p className="text-slate-900 font-medium">
                            {new Date(event.startsAt).toLocaleDateString('en-US', { 
                              weekday: 'long', 
                              year: 'numeric', 
                              month: 'long', 
                              day: 'numeric' 
                            })}
                          </p>
                          <p className="text-sm text-slate-500">
                            {new Date(event.startsAt).toLocaleTimeString('en-US', { 
                              hour: '2-digit', 
                              minute: '2-digit' 
                            })}
                          </p>
                        </div>
                      </div>
                      
                      <div className="flex items-start space-x-3">
                        <ClockIcon className="w-5 h-5 text-emerald-600 mt-1 flex-shrink-0" />
                        <div>
                          <p className="text-sm text-slate-600">End Date</p>
                          <p className="text-slate-900 font-medium">
                            {new Date(event.endsAt).toLocaleDateString('en-US', { 
                              weekday: 'long', 
                              year: 'numeric', 
                              month: 'long', 
                              day: 'numeric' 
                            })}
                          </p>
                          <p className="text-sm text-slate-500">
                            {new Date(event.endsAt).toLocaleTimeString('en-US', { 
                              hour: '2-digit', 
                              minute: '2-digit' 
                            })}
                          </p>
                        </div>
                      </div>
                      
                      <div className="flex items-start space-x-3">
                        <UsersIcon className="w-5 h-5 text-emerald-600 mt-1 flex-shrink-0" />
                        <div>
                          <p className="text-sm text-slate-600">Capacity</p>
                          <p className="text-slate-900 font-medium">{event.slots} slots</p>
                        </div>
                      </div>
                    </div>

                    {event.tags && event.tags.length > 0 && (
                      <div className="pt-4 border-t border-slate-200">
                        <p className="text-sm font-semibold text-slate-600 mb-3 flex items-center">
                          <TagIcon className="w-4 h-4 mr-2" />
                          Tags
                        </p>
                        <div className="flex flex-wrap gap-2">
                          {(Array.isArray(event.tags) ? event.tags : []).map((tag, index) => (
                            <span
                              key={index}
                              className="px-3 py-1 rounded-full bg-emerald-100 text-emerald-700 text-sm border border-emerald-200"
                            >
                              {tag}
                            </span>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              </div>

              {/* Sidebar */}
              <div className="space-y-6">
                {/* Status Card */}
                <div className="bg-white rounded-2xl border border-slate-200 p-6">
                  <h3 className="text-sm font-semibold text-slate-600 mb-4">Status</h3>
                  <span className={`inline-block px-4 py-2 rounded-full text-sm font-semibold ${getStatusBadge(event.status)}`}>
                    {event.status || 'DRAFT'}
                  </span>
                  {event.publishAt && (
                    <p className="text-xs text-slate-500 mt-3">
                      Published: {new Date(event.publishAt).toLocaleDateString()}
                    </p>
                  )}
                </div>

                {/* Quick Stats */}
                <div className="bg-white rounded-2xl border border-slate-200 p-6">
                  <h3 className="text-sm font-semibold text-slate-600 mb-4">Quick Stats</h3>
                  <div className="space-y-3">
                    <div>
                      <p className="text-xs text-slate-500">Created</p>
                      <p className="text-slate-900 font-medium">
                        {new Date(event.createdAt).toLocaleDateString()}
                      </p>
                    </div>
                    {event.updatedAt && event.updatedAt !== event.createdAt && (
                      <div>
                        <p className="text-xs text-slate-500">Last Updated</p>
                        <p className="text-slate-900 font-medium">
                          {new Date(event.updatedAt).toLocaleDateString()}
                        </p>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      </main>
    </div>
  )
}

export default CenterEventDetailPage
